import { useState, useRef } from "react";
import { Mic, Play, Square, RotateCcw, Volume2 } from "lucide-react";
import { earnXP } from "../gamification";
import lessons from "../data/lessons.json";
import PremiumGate, { isPremium } from "../components/PremiumGate";

export default function VoiceRecorder() {
  const [sentence, setSentence] = useState(() => {
    const day = Number(localStorage.getItem("currentDay") || 1);
    const lesson = lessons.find((l) => l.day === day) || lessons[0];
    return lesson.sentence;
  });
  const [isRecording, setIsRecording] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [nativeAudioPlaying, setNativeAudioPlaying] = useState(false);
  const mediaRecorder = useRef(null);
  const audioChunks = useRef([]);

  if (!isPremium()) return <PremiumGate feature="Voice Recorder & Comparison" />;

  function changeSentence() {
    const random = lessons[Math.floor(Math.random() * lessons.length)];
    setSentence(random.sentence);
    setAudioURL(null);
  }

  function speakNative() {
    if ("speechSynthesis" in window) {
      const u = new SpeechSynthesisUtterance(sentence);
      u.rate = Number(localStorage.getItem("speechRate") || "0.85");
      u.lang = "en-US";
      setNativeAudioPlaying(true);
      u.onend = () => setNativeAudioPlaying(false);
      window.speechSynthesis.speak(u);
    }
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorder.current = new MediaRecorder(stream);
      audioChunks.current = [];
      mediaRecorder.current.ondataavailable = (e) => audioChunks.current.push(e.data);
      mediaRecorder.current.onstop = () => {
        const blob = new Blob(audioChunks.current, { type: "audio/webm" });
        setAudioURL(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
        earnXP(10, "speaking");
      };
      mediaRecorder.current.start();
      setIsRecording(true);
    } catch {
      alert("Microphone access denied. Please allow microphone access.");
    }
  }

  function stopRecording() {
    if (mediaRecorder.current && isRecording) {
      mediaRecorder.current.stop();
      setIsRecording(false);
    }
  }

  function resetRecording() {
    setAudioURL(null);
  }

  return (
    <div className="space-y-6 page-enter relative">
      <div className="blob blob-rose" style={{ width: 320, height: 320, top: -40, right: -40, opacity: 0.18 }} />
      <div className="blob blob-purple" style={{ width: 260, height: 260, top: 280, left: -40, opacity: 0.15 }} />
      <div className="relative">
        <h1 className="font-display text-4xl md:text-5xl font-normal"><em>Voice recorder.</em></h1>
        <p className="text-gray-600 dark:text-gray-300 font-semibold mt-1">Record yourself and compare with native pronunciation</p>
      </div>

      {/* Target sentence */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <p className="text-sm text-gray-400 mb-2">Say this sentence:</p>
        <p className="text-xl font-bold text-gray-900 mb-4">"{sentence}"</p>
        <div className="flex gap-2">
          <button onClick={speakNative} className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${nativeAudioPlaying ? "bg-cream-100 text-gold-700" : "bg-gray-100 text-gray-700 hover:bg-paper-deep"}`}>
            <Volume2 className="w-4 h-4" /> {nativeAudioPlaying ? "Playing..." : "Listen Native"}
          </button>
          <button onClick={changeSentence} className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-all">
            <RotateCcw className="w-4 h-4" /> New Sentence
          </button>
        </div>
      </div>

      {/* Recorder */}
      <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
        <p className="text-sm text-gray-400 mb-6">{isRecording ? "Recording... Speak now!" : audioURL ? "Recording saved. Play it back!" : "Tap to record your voice"}</p>

        <div className="flex items-center justify-center gap-6">
          {!isRecording && !audioURL && (
            <button onClick={startRecording} className="w-20 h-20 bg-ink-900 text-white rounded-full flex items-center justify-center hover:bg-ink-900 hover:scale-105 shadow-lg shadow-paper-deep transition-all">
              <Mic className="w-8 h-8" />
            </button>
          )}

          {isRecording && (
            <button onClick={stopRecording} className="w-20 h-20 bg-berry text-white rounded-full flex items-center justify-center animate-pulse shadow-lg shadow-berry-light">
              <Square className="w-8 h-8" />
            </button>
          )}
        </div>

        {/* Playback */}
        {audioURL && (
          <div className="mt-6 space-y-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-sm font-semibold text-gray-700 mb-2">Your Recording</p>
              <audio src={audioURL} controls className="w-full" />
            </div>

            <div className="bg-paper-deep rounded-xl p-4">
              <p className="text-sm font-semibold text-gold-700 mb-2">Native Pronunciation</p>
              <button onClick={speakNative} className="w-full bg-cream-100 text-gold-700 py-3 rounded-lg font-medium hover:bg-cream-200 transition-all flex items-center justify-center gap-2">
                <Play className="w-4 h-4" /> Play Native Version
              </button>
            </div>

            <div className="flex gap-3">
              <button onClick={resetRecording} className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all">
                <RotateCcw className="w-4 h-4 inline mr-2" /> Record Again
              </button>
              <button onClick={changeSentence} className="flex-1 bg-ink-900 text-white py-3 rounded-xl font-semibold hover:bg-ink-900 transition-all">
                Next Sentence
              </button>
            </div>

            <p className="text-xs text-gray-400">Compare your recording with the native version. Focus on rhythm, stress, and clarity.</p>
          </div>
        )}
      </div>
    </div>
  );
}
